import {LatLng} from '../react-app-env';
import {getCenter, getDistance} from "geolib";

/**
 * Interface for GeoLib LatLng instances.
 */
interface GeoLibLatLng {
  longitude: number;
  latitude: number;
}

/**
 * Calculates the center of the given coordinates.
 * @param {LatLng[]} coords The coordinates to calculate the center for.
 */
export function centerOf(coords: LatLng[]): LatLng | undefined {
  const geoLibCenter = getCenter(coords.map(c => toGeoLibLatLng(c)));
  return geoLibCenter ? fromGeoLibLatLng(geoLibCenter) : undefined;
}

export function distanceBetween(coord1: LatLng, coord2: LatLng): number {
  return getDistance(toGeoLibLatLng(coord1), toGeoLibLatLng(coord2));
}

/**
 * Converts an FR LatLng to a GeoLib LatLng.
 * @param {LatLng} coord LatLng to convert.
 */
export function toGeoLibLatLng(coord: LatLng): GeoLibLatLng {
  return {
    latitude: coord.lat,
    longitude: coord.lng
  };
}

/**
 * Converts a GeoLib LatLng to an FR LatLng.
 * @param {LatLng} coord LatLng to convert.
 */
export function fromGeoLibLatLng(coord: GeoLibLatLng): LatLng {
  return {
    lat: coord.latitude,
    lng: coord.longitude
  };
}

/**
 * Everything from here onwards was liberated from google-map-react's lib/geo folder, since they don't export it.
 */

export function fitBounds({ ne, sw }: any, { width, height }: any) {
  const calculatedNwSe = convertNeSwToNwSe({ ne, sw });
  const fittedData = fitNwSe(calculatedNwSe.nw, calculatedNwSe.se, width, height);

  return {
    ...fittedData,
    newBounds: {
      ...fittedData.newBounds,
      ...convertNwSeToNeSw(fittedData.newBounds),
    },
  };
}

const log2 = Math.log2 ? Math.log2 : (x: any) => Math.log(x) / Math.LN2;
const GOOGLE_TILE_SIZE = 256;

function fitNwSe(nw: LatLng, se: LatLng, width: number, height: number): any {
  const EPS = 0.000000001;
  const nwWorld = latLng2World(nw);
  const seWorld = latLng2World(se);
  const dx =
            nwWorld.x < seWorld.x ? seWorld.x - nwWorld.x : 1 - nwWorld.x + seWorld.x;
  const dy = seWorld.y - nwWorld.y;

  if (dx <= 0 && dy <= 0) {
    return null;
  }

  const zoomX = log2(width / GOOGLE_TILE_SIZE / Math.abs(dx));
  const zoomY = log2(height / GOOGLE_TILE_SIZE / Math.abs(dy));
  const zoom = Math.floor(EPS + Math.min(zoomX, zoomY));

  // TODO find center just unproject middle world point
  const middle = {
    x: nwWorld.x < seWorld.x // eslint-disable-line
           ? 0.5 * (nwWorld.x + seWorld.x)
           : nwWorld.x + seWorld.x - 1 > 0
            ? 0.5 * (nwWorld.x + seWorld.x - 1)
            : 0.5 * (1 + nwWorld.x + seWorld.x),
    y: 0.5 * (nwWorld.y + seWorld.y),
  };

  const scale = Math.pow(2, zoom);
  const halfW = width / scale / GOOGLE_TILE_SIZE / 2;
  const halfH = height / scale / GOOGLE_TILE_SIZE / 2;

  const newNW = world2LatLng({
    x: middle.x - halfW,
    y: middle.y - halfH,
  });

  const newSE = world2LatLng({
    x: middle.x + halfW,
    y: middle.y + halfH,
  });

  return {
    center: world2LatLng(middle),
    zoom,
    newBounds: {
      nw: newNW,
      se: newSE,
    },
  };
}

export function convertNeSwToNwSe({ ne, sw }: any): any {
  return {
    nw: {
      lat: ne.lat,
      lng: sw.lng,
    },
    se: {
      lat: sw.lat,
      lng: ne.lng,
    },
  };
}

export function convertNwSeToNeSw({ nw, se }: any): any {
  return {
    ne: {
      lat: nw.lat,
      lng: se.lng,
    },
    sw: {
      lat: se.lat,
      lng: nw.lng,
    },
  };
}

function latLng2World({ lat, lng }: any): any {
  const sin = Math.sin((lat * Math.PI) / 180);
  const x = lng / 360 + 0.5;
  let y = 0.5 - (0.25 * Math.log((1 + sin) / (1 - sin))) / Math.PI;

  y = y < 0 // eslint-disable-line
      ? 0
      : y > 1
          ? 1
          : y;
  return { x, y };
}

function world2LatLng({ x, y }: any): any {
  const n = Math.PI - 2 * Math.PI * y;

  // TODO test that this is faster
  // 360 * Math.atan(Math.exp((180 - y * 360) * Math.PI / 180)) / Math.PI - 90;
  return {
    lat: (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n))),
    lng: x * 360 - 180,
  };
}
